import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  InputAdornment,
  Fade,
  Grow,
} from "@mui/material";
import { Email, ArrowBack, Send, VpnKey } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../router/routesModel";
import { forgotPassword } from "../service/userApi";
import { supabase, isSupabaseConfigured } from "../../lib/supabaseClient";
import { buildAuthRedirectUrl } from "../../lib/nativeAuth";
import { useKeyboardInset } from "../../native/keyboardInset";
import { claimRefusals } from "../../utils/refusalNotice";
import { dvh } from "../../styles/viewportHeight";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  // Android 15+ (API 36) draws edge-to-edge and no longer resizes the WebView
  // when the IME opens, so this 100dvh box keeps reporting the whole screen and
  // the email field sits behind the keyboard. Same fix, same reason, as the
  // sign-in and sign-up pages. See src/native/keyboardInset.js.
  const keyboardInset = useKeyboardInset();
  // B-35. Settings > Password opens this page for a signed-in collector.
  // Production changes a password the way it resets one, with an emailed
  // Supabase link, so the page itself is right; it just has to start from the
  // account's own email and lead back to Settings, not to a sign-in screen
  // they never left. Navigation state only exists for in-app navigation.
  const { state } = useLocation();
  const fromSettings = state?.fromSettings === true;
  // Back to sign-in keeps where the reader was going (a shared trade list's
  // offer, for one), which LoginPage handed over (JRN-1b).
  const goBack = () => {
    if (fromSettings) navigate(ROUTES.EDIT_USER);
    else if (state?.from) navigate(ROUTES.LOGIN, { state: { from: state.from } });
    else navigate(ROUTES.LOGIN);
  };
  const [data, setData] = useState({ email: (fromSettings && state.email) || "" });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleReset = () => {
    setData({ email: "" });
    setErrors({});
    setSuccess(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!data.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    return newErrors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {
      if (isSupabaseConfigured() && supabase) {
        // Native gets the custom scheme so tapping the reset link on the device
        // opens the app instead of https://localhost, which resolves to nothing
        // (#83). Web is unchanged.
        // `?type=recovery` is carried on our own redirect rather than left to
        // Supabase: under PKCE the link comes back as `?code=…` with no
        // fragment, so the fragment `type=recovery` the callback used to look
        // for never arrives. Marking the URL ourselves means the callback can
        // tell a reset apart from a sign-in no matter what Supabase appends.
        const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
          redirectTo: buildAuthRedirectUrl(`${ROUTES.AUTH_CALLBACK}?type=recovery`),
        });
        if (error) throw error.message;
      } else {
        await forgotPassword(data.email);
      }
      setSuccess(true);
      setData({ email: "" });
    } catch (error) {
      // Shown under the email field (B-31).
      claimRefusals();
      setErrors({ email: typeof error === "string" ? error : error?.toString?.() || "Failed to send reset email" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: dvh(100),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        // Scroll room for the keyboard. `overflow: hidden` only clips the
        // ::before glow — the Box is min-height, so it grows and the document
        // scrolls. 0 on web, which is what the card already computes to, so
        // nothing about the web layout moves.
        pb: `${keyboardInset}px`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 25% 40%, rgba(120, 119, 198, 0.3), transparent 50%), radial-gradient(circle at 75% 70%, rgba(138, 43, 226, 0.3), transparent 50%)",
          animation: "pulse 8s ease-in-out infinite",
        },
        "@keyframes pulse": {
          "0%, 100%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.8,
          },
        },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 500, px: 2, position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Fade in timeout={600}>
          <Button
            startIcon={<ArrowBack />}
            onClick={goBack}
            sx={{
              mb: 2,
              color: "white",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            {fromSettings ? "Back to Settings" : "Back to Login"}
          </Button>
        </Fade>

        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                py: 5,
                px: 4,
                textAlign: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -1,
                  left: 0,
                  right: 0,
                  height: 2,
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
                },
              }}
            >
              <Grow in timeout={1000}>
                <VpnKey sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
              </Grow>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  mb: 1,
                }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontWeight: 300,
                }}
              >
                We'll send you instructions to reset it
              </Typography>
              {/*
                p1-pres-07: a paragraph used to render here, gated on
                `isSupabaseConfigured() && supabase`, reading "Password reset
                uses Supabase Auth when this app is configured for beta. Check
                spam folders for mail from Supabase — not the legacy Mongo-only
                reset flow." It named the auth vendor twice and the previous
                storage engine once, on the screen a locked-out beta tester
                lands on, and asked them to tell two backend implementations
                apart. Which reset flow runs is not a fact about the reader.

                Deleted rather than rewritten. The one thing in it a user could
                have acted on was "check spam folders", and re-wording that into
                new copy is the owner's call, not this routine's — it is written
                up as an option in .claude/fix-sprint-log.md under sprint 64,
                not silently reinstated.
                `ForgotPasswordPage.jargon.test.jsx` renders this page with the
                module mocked as CONFIGURED (the branch this block lived on) and
                fails on any of the five names it enumerates. It is a closed
                list, not a general vendor detector, and it covers the initial
                render only — see that file's header for what it misses.

                THIS DOES NOT CLOSE p1-pres-07. The same flow still names PKCE
                and Supabase one screen later: AuthCallbackPage.jsx:40's
                PKCE_HELP_COPY is appended at :349, :387, :404, :423 and :470
                into finalizeFailure, which sets both the page message and an
                error snackbar, and that message renders at :787 as primary
                copy — :349 being the stale-reset-link branch of this very
                journey.
              */}
            </Box>

            {/* Content Section */}
            <Box sx={{ p: 4 }}>
              {success ? (
                <Fade in timeout={600}>
                  <Box sx={{ textAlign: "center" }}>
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        "& .MuiAlert-icon": {
                          fontSize: 28,
                        },
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Email Sent!
                      </Typography>
                      <Typography variant="body2">
                        Please check your inbox and follow the instructions to
                        reset your password. The link will expire in 1 hour.
                      </Typography>
                    </Alert>
                    <Button
                      variant="outlined"
                      onClick={goBack}
                      fullWidth
                      sx={{
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        borderWidth: 2,
                        "&:hover": {
                          borderWidth: 2,
                        },
                      }}
                    >
                      {fromSettings ? "Back to Settings" : "Return to Login"}
                    </Button>
                  </Box>
                </Fade>
              ) : (
                <Box component="form" onSubmit={onSubmit}>
                  <Fade in timeout={1200}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, textAlign: "center", lineHeight: 1.6 }}
                    >
                      Enter your email address and we'll send you a secure link
                      to reset your password.
                    </Typography>
                  </Fade>

                  <Fade in timeout={1400}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={data.email}
                      onChange={handleInputChange}
                      error={Boolean(errors.email)}
                      helperText={typeof errors.email === "string" ? errors.email : errors.email?.message ?? ""}
                      disabled={isLoading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: "primary.main" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 3,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          transition: "all 0.3s",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.2)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 20px rgba(102, 126, 234, 0.3)",
                          },
                        },
                      }}
                    />
                  </Fade>

                  <Fade in timeout={1600}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      endIcon={<Send />}
                      disabled={isLoading || !data.email}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        boxShadow: "0 4px 15px 0 rgba(102, 126, 234, 0.4)",
                        transition: "all 0.3s",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 6px 25px 0 rgba(102, 126, 234, 0.6)",
                          background:
                            "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                        },
                        "&:disabled": {
                          background: "rgba(0, 0, 0, 0.12)",
                        },
                      }}
                    >
                      {isLoading ? "Sending..." : "Send Reset Link"}
                    </Button>
                  </Fade>
                </Box>
              )}
            </Box>

            {/* Footer Wave */}
            <Box
              sx={{
                height: 8,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            />
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;

